import React from 'react';

interface WorkerPerformanceProps {
  dailySummary: any;
  userRole: string;
}

const WorkerPerformance: React.FC<WorkerPerformanceProps> = ({ dailySummary, userRole }) => {
  if (userRole !== 'manager' && userRole !== 'executive1') {
    return null;
  }

  const workers = dailySummary?.workers || [];
  const topPerformers = workers
    .sort((a: any, b: any) => (b.items_picked || 0) - (a.items_picked || 0))
    .slice(0, 5);

  const totalItemsPicked = workers.reduce((sum: number, worker: any) => sum + (worker.items_picked || 0), 0);
  const averagePerformance = workers.length > 0 ? Math.round(totalItemsPicked / workers.length) : 0;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Worker Performance Today</h3>
      </div>
      <div className="p-6">
        {/* Performance Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{workers.length}</div>
            <div className="text-sm text-gray-500">Active Workers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{totalItemsPicked}</div>
            <div className="text-sm text-gray-500">Total Items Picked</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{averagePerformance}</div>
            <div className="text-sm text-gray-500">Average per Worker</div>
          </div>
        </div>

        {/* Top Performers */}
        {topPerformers.length > 0 ? (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Top Performers</h4>
            <div className="space-y-3">
              {topPerformers.map((worker: any, index: number) => (
                <div key={worker.worker_id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      index === 2 ? 'bg-orange-400' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {worker.worker_name || `Worker ${worker.worker_id}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {worker.worker_id}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {worker.items_picked || 0}
                    </div>
                    <div className="text-xs text-gray-500">items picked</div>
                  </div>
                </div>
              ))}
            </div>
          </div>        ) : (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-gray-400 font-bold text-lg">W</span>
            </div>
            <p className="text-sm text-gray-500">No worker data available</p>
          </div>
        )}

        {/* Performance Chart Placeholder */}
        {workers.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Performance Distribution</h4>
            <div className="space-y-2">
              {topPerformers.map((worker: any, index: number) => {
                const percentage = totalItemsPicked > 0 ? (worker.items_picked / totalItemsPicked) * 100 : 0;
                return (
                  <div key={worker.worker_id || index}>
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{worker.worker_name || `Worker ${worker.worker_id}`}</span>
                      <span>{percentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-gray-400' : 
                          index === 2 ? 'bg-orange-400' : 'bg-blue-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerPerformance;
